// TourProvider — the shared guided-tour engine (WP4).
//
// Mounted once inside the AppShell (which is already inside the router), it owns
// the active tour + step index, persists them to sessionStorage so a mid-tour
// reload resumes (and the session end clears them), listens for the
// `prime:start-tour` custom event dispatched by the parallel welcome/home
// packages, filters a tour's steps by the signed-in user's RBAC, drives
// route + anchor navigation for each step, and exposes useTours() for the panel.
//
// Missing-anchor grace is a hard contract: if a step's anchor cannot be resolved
// within the retry window, the panel still renders the step (with a fallback
// note) and Back/Next keep working — nothing throws and nothing logs an error.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { markTourCompleted } from '../../lib/onboardingState.js';
import { getTour } from '../../content/tours/index.js';
import { ANCHORS } from './tourAnchors.js';

export const TOUR_STORAGE_KEY = 'dha_prime__tour_v1';
export const START_TOUR_EVENT = 'prime:start-tour';

// Anchor resolution retries until an element appears (routes mount async after a
// navigate). ~2s total budget, polled every 150ms, before missing-anchor grace.
const ANCHOR_TIMEOUT_MS = 2000;
const ANCHOR_POLL_MS = 150;
const ANCHOR_ACTIVE_CLASS = 'tour-anchor-active';

const TourContext = createContext(null);

function readPersisted() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(TOUR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.activeTourId !== 'string') return null;
    return {
      activeTourId: parsed.activeTourId,
      stepIndex: Number.isInteger(parsed.stepIndex) ? parsed.stepIndex : 0,
    };
  } catch {
    return null;
  }
}

function writePersisted(state) {
  if (typeof window === 'undefined') return;
  try {
    if (!state || !state.activeTourId) {
      window.sessionStorage.removeItem(TOUR_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(
        TOUR_STORAGE_KEY,
        JSON.stringify({ activeTourId: state.activeTourId, stepIndex: state.stepIndex }),
      );
    }
  } catch {
    /* sessionStorage unavailable — the tour still runs, it just won't resume */
  }
}

// Steps a user may actually reach, given their roles. A step whose routeKey the
// user cannot see is dropped, so the panel's step count reflects reality.
function visibleSteps(tour, roles) {
  if (!tour) return [];
  return tour.steps.filter((step) => rbac.canSeeRoute(step.routeKey, roles));
}

export function TourProvider({ children }) {
  const { user, roles = [] } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();

  // Refs keep the event handler + step callbacks stable while still reading the
  // latest roles/user/state without re-subscribing every render.
  const rolesRef = useRef(roles);
  const userIdRef = useRef(user?.id || null);
  rolesRef.current = roles;
  userIdRef.current = user?.id || null;

  const [state, setState] = useState(() => {
    const persisted = readPersisted();
    return persisted
      ? { activeTourId: persisted.activeTourId, stepIndex: persisted.stepIndex, showClosing: false }
      : { activeTourId: null, stepIndex: 0, showClosing: false };
  });
  const [anchorMissing, setAnchorMissing] = useState(false);

  const activeTour = getTour(state.activeTourId);
  const steps = useMemo(() => visibleSteps(activeTour, roles), [activeTour, roles]);
  const boundedIndex = Math.min(state.stepIndex, Math.max(0, steps.length - 1));
  const currentStep = steps[boundedIndex] || null;
  const stepCount = steps.length;

  // Persist on every meaningful state change (and clear when the tour stops).
  useEffect(() => {
    writePersisted(state.activeTourId ? state : null);
  }, [state]);

  // Validate a resumed/persisted tour once roles are known: an unknown tour id or
  // a tour with zero visible steps for this user is cleared rather than left in a
  // broken state.
  useEffect(() => {
    if (!state.activeTourId) return;
    if (!activeTour || steps.length === 0) {
      setState({ activeTourId: null, stepIndex: 0, showClosing: false });
    }
  }, [state.activeTourId, activeTour, steps.length]);

  const start = useCallback((tourId) => {
    const tour = getTour(tourId);
    if (!tour) {
      console.debug('[tours] ignoring start for unknown tour', tourId);
      return;
    }
    const reachable = visibleSteps(tour, rolesRef.current);
    if (reachable.length === 0) {
      console.debug('[tours] no visible steps for tour', tourId, '- not starting');
      return;
    }
    setAnchorMissing(false);
    setState({ activeTourId: tourId, stepIndex: 0, showClosing: false });
  }, []);
  const startRef = useRef(start);
  startRef.current = start;

  const stop = useCallback(() => {
    setAnchorMissing(false);
    setState({ activeTourId: null, stepIndex: 0, showClosing: false });
  }, []);

  const next = useCallback(() => {
    setState((prev) => {
      const tour = getTour(prev.activeTourId);
      if (!tour) return prev;
      const reachable = visibleSteps(tour, rolesRef.current);
      const lastIndex = Math.max(0, reachable.length - 1);
      if (prev.showClosing) return prev;
      if (prev.stepIndex >= lastIndex) {
        // Finish (never Skip): record completion, then reveal the closing pane.
        if (userIdRef.current) markTourCompleted(userIdRef.current, prev.activeTourId);
        return { ...prev, stepIndex: lastIndex, showClosing: true };
      }
      return { ...prev, stepIndex: prev.stepIndex + 1 };
    });
  }, []);

  const back = useCallback(() => {
    setState((prev) => {
      if (prev.showClosing) return { ...prev, showClosing: false };
      if (prev.stepIndex <= 0) return prev;
      return { ...prev, stepIndex: prev.stepIndex - 1 };
    });
  }, []);

  // Global start trigger from the parallel welcome overlay / home strip.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (event) => {
      const tourId = event?.detail?.tourId;
      if (typeof tourId === 'string' && tourId) startRef.current(tourId);
    };
    window.addEventListener(START_TOUR_EVENT, handler);
    return () => window.removeEventListener(START_TOUR_EVENT, handler);
  }, []);

  // Route + anchor driver. Navigates to the step's route when needed, then polls
  // for its anchor. Found → highlight + scroll into view. Not found within the
  // budget → missing-anchor grace (panel keeps rendering; no throw, no error).
  useEffect(() => {
    if (!currentStep || state.showClosing) {
      setAnchorMissing(false);
      return undefined;
    }
    if (location.pathname !== currentStep.route) {
      navigate(currentStep.route);
      return undefined; // re-runs once the location settles on the step route
    }

    setAnchorMissing(false);
    let cancelled = false;
    let timer = null;
    let highlighted = null;
    const startedAt = Date.now();

    const attempt = () => {
      if (cancelled) return;
      let el = null;
      try {
        el = document.querySelector(currentStep.anchor);
      } catch {
        el = null; // malformed selector must never throw the tour down
      }
      if (el) {
        highlighted = el;
        el.classList.add(ANCHOR_ACTIVE_CLASS);
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        setAnchorMissing(false);
        return;
      }
      if (Date.now() - startedAt >= ANCHOR_TIMEOUT_MS) {
        setAnchorMissing(true); // grace: render the step + fallback note
        return;
      }
      timer = setTimeout(attempt, ANCHOR_POLL_MS);
    };
    timer = setTimeout(attempt, 0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (highlighted) highlighted.classList.remove(ANCHOR_ACTIVE_CLASS);
    };
  }, [currentStep, state.showClosing, location.pathname, navigate]);

  const value = useMemo(
    () => ({
      activeTourId: state.activeTourId,
      stepIndex: boundedIndex,
      steps,
      stepCount,
      currentStep,
      tour: activeTour,
      showClosing: state.showClosing,
      anchorMissing,
      start,
      stop,
      next,
      back,
    }),
    [
      state.activeTourId,
      state.showClosing,
      boundedIndex,
      steps,
      stepCount,
      currentStep,
      activeTour,
      anchorMissing,
      start,
      stop,
      next,
      back,
    ],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTours() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTours must be used inside a TourProvider');
  return ctx;
}

// Re-export so callers importing the provider also get the anchor catalog if
// they need to author or test steps.
export { ANCHORS };
